<template>
  <gas-page-shell :breadcrumb="['首页', '概览']">
    <div v-loading="loading" class="overview-content">
      <div class="stat-row">
        <div v-for="item in statCards" :key="item.label" class="stat-card">
          <div class="stat-text">
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-value">{{ item.value }}</div>
          </div>
          <div class="stat-icon" :style="{ background: item.bg, color: item.color }">
            <i :class="item.icon"></i>
          </div>
        </div>
      </div>

      <div class="overview-main">
        <section class="panel online-panel">
          <div class="panel-title">终端在线状态</div>
          <div class="panel-body distribution-body">
            <div class="pie" :style="pieStyle">
              <div class="pie-center">
                <span>{{ onlineRateText }}</span>
                <small>在线率</small>
              </div>
            </div>
            <ul class="pie-labels">
              <li v-for="item in distributionRows" :key="item.key">
                <i :class="item.key"></i>
                <span>{{ item.label }}</span>
                <strong>{{ formatNumber(item.value) }}</strong>
              </li>
            </ul>
          </div>

          <div class="status-breakdown project-device-breakdown">
            <div class="status-section nested-status-section">
              <div class="status-section-head">
                <span>按项目分类 / 设备类型</span>
                <small>{{ projectDeviceTypeStatusRows.length }} 类</small>
              </div>
              <div v-if="projectDeviceTypeStatusRows.length" class="project-status-list">
                <div v-for="category in projectDeviceTypeStatusRows" :key="`category-${category.id || category.name}`" class="project-status-card">
                  <div class="project-status-head">
                    <div class="project-title">
                      <span>{{ category.name }}</span>
                      <small>{{ category.children.length }} 个设备类型</small>
                    </div>
                    <strong>{{ category.rateText }}</strong>
                  </div>
                  <div class="status-bar">
                    <i :style="{ width: category.rateWidth }"></i>
                  </div>
                  <div class="status-counts">
                    <span>总数 {{ formatNumber(category.total) }}</span>
                    <span class="ok">在线 {{ formatNumber(category.online) }}</span>
                    <span>离线 {{ formatNumber(category.offline) }}</span>
                  </div>
                  <div v-if="category.children.length" class="device-type-list">
                    <div v-for="child in category.children" :key="`${category.name}-${child.name}`" class="device-type-row">
                      <div class="device-type-head">
                        <span class="status-name">{{ child.name }}</span>
                        <strong>{{ child.rateText }}</strong>
                      </div>
                      <div class="status-bar thin">
                        <i :style="{ width: child.rateWidth }"></i>
                      </div>
                      <div class="status-counts compact-counts">
                        <span>总数 {{ formatNumber(child.total) }}</span>
                        <span class="ok">在线 {{ formatNumber(child.online) }}</span>
                        <span>离线 {{ formatNumber(child.offline) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-tip compact">暂无数据</div>
            </div>
          </div>
        </section>

        <div class="overview-side">
          <section class="panel metric-panel">
            <div class="panel-title">运行指标</div>
            <div class="metric-grid">
              <div v-for="item in metricRows" :key="item.label" class="metric-item">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </section>

          <section class="panel latest-panel">
            <div class="panel-title">最新未处理告警</div>
            <div v-if="latestAlarms.length" class="alarm-list">
              <div v-for="item in latestAlarms" :key="item.alarmId" class="alarm-row">
                <span class="alarm-level" :class="levelClass(item.alarmLevel)">{{ levelText(item.alarmLevel) }}</span>
                <div class="alarm-main">
                  <div class="alarm-title">{{ item.title || '未命名告警' }}</div>
                  <div class="alarm-meta">{{ item.siteName || '-' }} · {{ formatTime(item.alarmTime) }}</div>
                </div>
              </div>
            </div>
            <div v-else class="empty-tip">暂无未处理告警</div>
          </section>
        </div>
      </div>
    </div>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import { gasApi } from '@/api/gas'

export default {
  components: { GasPageShell },
  data() {
    return {
      loading: false,
      overview: {}
    }
  },
  computed: {
    terminalTotal() {
      return this.numberValue(this.overview.terminalTotal, this.overview.total)
    },
    terminalOnline() {
      return this.numberValue(this.overview.terminalOnline, this.overview.online)
    },
    terminalOffline() {
      return this.numberValue(this.overview.terminalOffline, this.overview.offline)
    },
    alarmTerminalCount() {
      return this.numberValue(this.overview.alarmTerminalCount, this.overview.alarm)
    },
    openAlarmCount() {
      return this.numberValue(this.overview.openAlarmCount, this.overview.alarm)
    },
    statCards() {
      return [
        { label: '总终端数', value: this.formatNumber(this.terminalTotal), icon: 'el-icon-monitor', color: '#1677ff', bg: '#e8f3ff' },
        { label: '在线终端', value: this.formatNumber(this.terminalOnline), icon: 'el-icon-circle-check', color: '#00a870', bg: '#e8ffea' },
        { label: '离线终端', value: this.formatNumber(this.terminalOffline), icon: 'el-icon-circle-close', color: '#86909c', bg: '#f2f3f5' },
        { label: '未处理告警', value: this.formatNumber(this.openAlarmCount), icon: 'el-icon-warning-outline', color: '#ff7d00', bg: '#fff7e8' }
      ]
    },
    distributionRows() {
      const rows = Array.isArray(this.overview.distribution) && this.overview.distribution.length
        ? this.overview.distribution
        : [
          { key: 'online', label: '在线', value: this.terminalOnline },
          { key: 'offline', label: '离线', value: this.terminalOffline }
        ]
      return rows.map(item => ({
        key: item.key,
        label: item.label,
        value: this.numberValue(item.value)
      }))
    },
    distributionTotal() {
      return this.distributionRows.reduce((sum, item) => sum + item.value, 0)
    },
    onlineRateText() {
      const rate = this.numberValue(this.overview.onlineRate)
      if (rate > 0 || this.terminalTotal > 0) {
        const value = rate > 0 ? rate : (this.terminalOnline * 100 / Math.max(this.terminalTotal, 1))
        return `${value.toFixed(1)}%`
      }
      return '0%'
    },
    pieStyle() {
      if (!this.distributionTotal) {
        return {
          background: 'radial-gradient(circle, #fff 0 52%, transparent 53%), conic-gradient(#e5e6eb 0 100%)'
        }
      }
      const online = this.percentFor('online')
      const offlineEnd = online + this.percentFor('offline')
      return {
        background: `radial-gradient(circle, #fff 0 52%, transparent 53%), conic-gradient(#00b42a 0 ${online}%, #c9cdd4 ${online}% ${offlineEnd}%, #e5e6eb ${offlineEnd}% 100%)`
      }
    },
    metricRows() {
      return [
        { label: '组织数量', value: this.formatNumber(this.overview.orgTotal) },
        { label: '设备总数', value: this.formatNumber(this.overview.deviceTotal) },
        { label: '在线设备', value: this.formatNumber(this.overview.deviceOnline) },
        { label: '物联网卡', value: this.formatNumber(this.overview.iotCardTotal) },
        { label: '活跃卡数', value: this.formatNumber(this.overview.iotCardActive) },
        { label: '今日上报', value: this.formatNumber(this.overview.todayMessageCount) },
        { label: '今日告警', value: this.formatNumber(this.overview.todayAlarmCount) }
      ]
    },
    projectDeviceTypeStatusRows() {
      const rows = this.overview.terminalOnlineByProjectCategoryAndDeviceType
      if (Array.isArray(rows) && rows.length) {
        if (Array.isArray(rows[0].children)) {
          return rows.map(category => ({
            ...this.normalizeStatusRow(category),
            id: category.id,
            children: this.normalizeStatusRows(category.children)
          })).filter(category => category.name !== '未分类')
        }
        return this.buildProjectDeviceTypeRows(rows)
      }
      return this.normalizeStatusRows(this.overview.terminalOnlineByProjectCategory).map(category => ({
        ...category,
        id: category.id,
        children: []
      })).filter(category => category.name !== '未分类')
    },
    latestAlarms() {
      return Array.isArray(this.overview.latestAlarms) ? this.overview.latestAlarms : []
    }
  },
  created() {
    this.loadOverview()
  },
  methods: {
    async loadOverview() {
      this.loading = true
      try {
        const res = await gasApi.overview()
        this.overview = res.data || {}
      } finally {
        this.loading = false
      }
    },
    percentFor(key) {
      const item = this.distributionRows.find(row => row.key === key)
      if (!item || !this.distributionTotal) return 0
      return Number((item.value * 100 / this.distributionTotal).toFixed(2))
    },
    numberValue(...values) {
      for (const value of values) {
        const n = Number(value)
        if (Number.isFinite(n)) return n
      }
      return 0
    },
    formatNumber(value) {
      return this.numberValue(value).toLocaleString('zh-CN')
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    },
    normalizeStatusRow(row = {}) {
      const total = this.numberValue(row.total)
      const online = this.numberValue(row.online)
      const offline = this.numberValue(row.offline, Math.max(total - online, 0))
      const rate = total > 0 ? online * 100 / total : 0
      return {
        id: row.id,
        name: row.name || row.categoryName || row.deviceTypeName || row.groupName || '未分类',
        total,
        online,
        offline,
        rateText: `${rate.toFixed(1)}%`,
        rateWidth: `${Math.min(100, Math.max(0, rate)).toFixed(1)}%`
      }
    },
    normalizeStatusRows(rows) {
      if (!Array.isArray(rows)) return []
      return rows.map(row => this.normalizeStatusRow(row))
    },
    buildProjectDeviceTypeRows(rows) {
      if (!Array.isArray(rows)) return []
      const categories = new Map()
      rows.forEach(row => {
        const id = row.categoryId || row.projectCategoryId || row.id
        const name = row.categoryName || row.projectCategoryName || row.groupName || '未分类'
        const key = `${id || ''}|${name}`
        if (!categories.has(key)) {
          categories.set(key, { id, name, total: 0, online: 0, offline: 0, children: [] })
        }
        const category = categories.get(key)
        const child = this.normalizeStatusRow({
          name: row.deviceTypeName || row.deviceType || row.terminalType || '未分类',
          total: row.total,
          online: row.online,
          offline: row.offline
        })
        category.total += child.total
        category.online += child.online
        category.offline += child.offline
        category.children.push(child)
      })
      return Array.from(categories.values()).map(category => ({
        ...this.normalizeStatusRow(category),
        id: category.id,
        children: category.children
      })).filter(category => category.name !== '未分类')
    },
    levelText(level) {
      return ({ critical: '严重', major: '重要', warning: '警告', minor: '一般' })[level] || '告警'
    },
    levelClass(level) {
      return level || 'warning'
    }
  }
}
</script>

<style scoped>
.overview-content {
  position: relative;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}
.stat-card {
  min-height: 92px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.stat-label {
  color: #86909c;
  font-size: 14px;
}
.stat-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 600;
  color: #1d2129;
  line-height: 1.1;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 22px;
}

.overview-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 16px;
  align-items: stretch;
  min-height: calc(100vh - 260px);
}
.panel {
  padding: 22px;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.online-panel {
  min-height: calc(100vh - 260px);
  display: flex;
  flex-direction: column;
}
.overview-side {
  min-width: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 16px;
}
.panel-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1d2129;
}
.distribution-body {
  display: flex;
  gap: 38px;
  align-items: center;
}
.pie {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(29, 33, 41, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pie-center {
  text-align: center;
}
.pie-center span {
  display: block;
  font-size: 26px;
  font-weight: 600;
  color: #1d2129;
}
.pie-center small {
  display: block;
  margin-top: 4px;
  color: #86909c;
  font-size: 13px;
}
.pie-labels {
  list-style: none;
  padding: 0;
  margin: 0;
  color: #4e5969;
  min-width: 160px;
}
.pie-labels li {
  display: grid;
  grid-template-columns: 16px 1fr auto;
  align-items: center;
  gap: 8px;
  min-height: 34px;
}
.pie-labels i {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.pie-labels strong {
  color: #1d2129;
  font-weight: 600;
}
.pie-labels i.online { background: #00b42a; }
.pie-labels i.offline { background: #c9cdd4; }
.pie-labels i.alarm { background: #ff7d00; }

.status-breakdown {
  flex: 1;
  min-height: 0;
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.project-device-breakdown {
  grid-template-columns: minmax(0, 1fr);
}
.status-section {
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #f2f3f5;
  padding-top: 16px;
}
.nested-status-section {
  min-height: 360px;
}
.status-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: #1d2129;
  font-weight: 600;
}
.status-section-head small {
  color: #86909c;
  font-weight: 400;
}
.status-list {
  min-height: 0;
  overflow: auto;
  display: grid;
  align-content: start;
  gap: 10px;
  padding-right: 4px;
}
.project-status-list {
  min-height: 0;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
  gap: 12px;
  padding-right: 4px;
}
.status-row {
  border: 1px solid #f2f3f5;
  border-radius: 8px;
  padding: 12px;
  background: #fbfcfe;
}
.project-status-card {
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
}
.project-status-head,
.device-type-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.project-title {
  min-width: 0;
}
.project-title span {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #1d2129;
  font-size: 15px;
  font-weight: 600;
}
.project-title small {
  display: block;
  margin-top: 3px;
  color: #86909c;
  font-size: 12px;
  font-weight: 400;
}
.project-status-head strong,
.device-type-head strong {
  flex-shrink: 0;
  color: #00a870;
  font-size: 14px;
}
.device-type-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
  padding-left: 14px;
  border-left: 2px solid #eef1f6;
}
.device-type-row {
  padding: 10px 12px;
  border-radius: 8px;
  background: #fbfcfe;
  border: 1px solid #f2f3f5;
}
.status-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.status-name {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #1d2129;
  font-weight: 500;
}
.status-row-head strong {
  color: #00a870;
  font-size: 14px;
}
.status-bar {
  height: 8px;
  overflow: hidden;
  border-radius: 8px;
  background: #e5e6eb;
}
.status-bar.thin {
  height: 6px;
}
.status-bar i {
  display: block;
  height: 100%;
  border-radius: 8px;
  background: #00b42a;
}
.status-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 8px;
  color: #86909c;
  font-size: 12px;
}
.compact-counts {
  margin-top: 6px;
}
.status-counts .ok {
  color: #00a870;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.metric-item {
  min-height: 68px;
  border: 1px solid #f2f3f5;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fbfcfe;
}
.metric-item span {
  display: block;
  color: #86909c;
  font-size: 13px;
}
.metric-item strong {
  display: block;
  margin-top: 8px;
  color: #1d2129;
  font-size: 20px;
  line-height: 1.2;
}

.latest-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.alarm-list {
  flex: 1;
  display: grid;
  align-content: start;
  gap: 10px;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
}
.alarm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 54px;
  border: 1px solid #f2f3f5;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}
.alarm-level {
  min-width: 44px;
  height: 24px;
  line-height: 22px;
  border-radius: 4px;
  border: 1px solid #ffd591;
  background: #fff7e6;
  color: #d46b08;
  text-align: center;
  font-size: 12px;
  flex-shrink: 0;
}
.alarm-level.critical,
.alarm-level.major {
  border-color: #ffa39e;
  background: #fff1f0;
  color: #cf1322;
}
.alarm-main {
  min-width: 0;
}
.alarm-title {
  color: #1d2129;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.alarm-meta {
  margin-top: 4px;
  color: #86909c;
  font-size: 12px;
}
.empty-tip {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #86909c;
  background: #fbfcfe;
  border: 1px dashed #d9dee8;
  border-radius: 8px;
}
.empty-tip.compact {
  min-height: 54px;
}

@media (max-width: 1200px) {
  .stat-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .overview-main {
    grid-template-columns: 1fr;
    min-height: auto;
  }
  .online-panel {
    min-height: auto;
  }
}

@media (max-width: 768px) {
  .overview-content {
    padding: 16px;
  }
  .stat-row,
  .metric-grid,
  .status-breakdown,
  .project-status-list {
    grid-template-columns: 1fr;
  }
  .distribution-body {
    flex-direction: column;
    align-items: flex-start;
  }
  .pie {
    width: 180px;
    height: 180px;
  }
}
</style>
